import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { BlockNoteEditor } from '@blocknote/core';
import { encountersApi,useGetEncounterQuery } from '@features/encounters';
import {
  copyJotDocSections,
  JotDocNoteSectionView,
  jotdocsApi,
  useGetJotDocQuery,
  useJotdocSections,
  useJotDocSectionSave,
  useStreamingSections,
} from '@features/jotdocs';
import NextIndicatorButton from '@features/jotdocs/components/NextIndicatorButton';
import { findReplaceInMarkdown } from '@features/jotdocs/utils/findReplace';
import {
  createMisspellingClickHandler,
  detectMisspellingsInMarkdown,
} from '@features/jotdocs/utils/misspelling-utils';
import { useAccessToken,useUser } from '@features/user';
import { useUserPreferences } from '@features/user-preferences';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import {
  ContentCopy as ContentCopyIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  Spellcheck as SpellcheckIcon,
} from '@mui/icons-material';
import { Box, CircularProgress, ListItem, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector, useModal } from '@store/hooks';
import { useSnackbar } from 'notistack';

import LayoutWrapper from '../../../components/layout/UILayout';
import type { JotDocDataType } from '../../../components/Markdown/JotDocViewer';
import { MODAL_TYPES } from '../../../components/modals';
import NoteDocumentPicker from '../../../components/Note/NoteDocumentPicker';
import { SVGIcon } from '../../../components/SVGIcon';
import { clearFindReplace } from '../../../store/slices/findReplaceSlice';
import { RoundedButton } from '../../../styles/CustomButtons';
import { hiddenScrollbarStyles } from '../../../styles/globalStyles';
import { useNoteWithPolling } from '../hooks/useNoteWithPolling';

interface LocationState {
  noteId?: string;
  encounterId?: string;
}

const NoteJotDocView: React.FC = () => {
  const { jotdocId } = useParams<{ jotdocId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { open: openModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();

  // Get context from location state
  const locationState = location.state as LocationState | undefined;
  const [noteId] = useState<string | undefined>(locationState?.noteId);
  const [encounterId, setEncounterId] = useState<string | undefined>(locationState?.encounterId);

  // Note context state
  const [noteData, setNoteData] = useState<any>(null);

  // Table of contents state
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const sectionsContainerRef = useRef<HTMLDivElement>(null);
  const [gutterLeft, setGutterLeft] = useState<number>(0);
  const [isScrolling, setIsScrolling] = useState(false);

  // BlockNote editors ref (populated by JotDocNoteSectionView)
  const blockNoteEditorsRef = useRef<Map<string, BlockNoteEditor>>(new Map());

  // JotDoc save hook
  const { saveMarkdown } = useJotDocSectionSave();

  // Redux hooks for find/replace
  const dispatch = useAppDispatch();
  const findReplaceAction = useAppSelector(state => state.findReplace.action);

  // User preferences
  const { preferences: userPreferences } = useUserPreferences();

  // User trouble words for misspelling detection
  const { troubleWords } = useUser();

  // Access token for SSE streaming
  const { getAccessToken } = useAccessToken();

  // Fetch jotdoc using RTK Query
  const {
    data: jotdocResponse,
    isLoading: jotdocLoading,
    isError: jotdocIsError,
    error: jotdocQueryError,
    refetch: refetchJotdoc,
  } = useGetJotDocQuery({ jotdocId: jotdocId! }, { skip: !jotdocId });

  // Extract jotdoc data and encounter_id from RTK Query response
  const jotdoc: JotDocDataType | null = jotdocResponse?.jotdoc
    ? {
        jotdoc_id: jotdocResponse.jotdoc.jotdoc_id,
        title: jotdocResponse.jotdoc.title || '',
        content: jotdocResponse.jotdoc.content || '',
        created_at: jotdocResponse.jotdoc.created_at || '',
      }
    : null;

  // Extract encounter_id from jotdoc response (top-level field)
  // Always update if we get it from the response (even if already set from location.state)
  useEffect(() => {
    if (jotdocResponse?.encounter_id) {
      setEncounterId(jotdocResponse.encounter_id);
    }
  }, [jotdocResponse?.encounter_id]);

  // Fetch note and encounter context if we have noteId
  const { data: fetchedNoteData } = useNoteWithPolling(noteId, {
    skip: !noteId,
  });

  useEffect(() => {
    if (fetchedNoteData) {
      setNoteData(fetchedNoteData);
      // Extract encounter_id from note data
      // Check if note response has encounter_id field (if backend adds it)
      const noteEncounterId =
        (fetchedNoteData as any).encounter_id || (fetchedNoteData as any).encounter?.encounter_id;
      if (noteEncounterId) {
        setEncounterId(noteEncounterId);
      }
    }
  }, [fetchedNoteData]);

  // Fetch encounter using RTK Query when we have encounterId (for document picker context)
  const {
    data: encounterData,
    isLoading: encounterLoading,
    refetch: refetchEncounter,
  } = useGetEncounterQuery({ encounterId: encounterId! }, { skip: !encounterId });

  // Check if current jotdoc is pending using jotdoc's own status and thread_id fields
  // The backend now includes status, task_id, and thread_id directly on the JotDoc model
  const jotdocStatus = (jotdocResponse?.jotdoc as { status?: 'pending' | 'completed' })?.status;
  const threadId = (jotdocResponse?.jotdoc as { thread_id?: string | null })?.thread_id ?? null;
  const taskId = (jotdocResponse?.jotdoc as { task_id?: string | null })?.task_id ?? null;
  const isPending = jotdocStatus === 'pending' && Boolean(threadId);

  // Streaming sections parser for pending jotdocs
  const {
    sections: streamingSections,
    accumulatedContent: streamingAccumulatedContent,
    isStreaming: isStreamingSections,
    processDelta: processStreamingDelta,
    markComplete: markStreamingComplete,
    reset: resetStreamingSections,
  } = useStreamingSections();

  // Reset streaming when jotdoc completes or changes
  const prevIsPendingRef = useRef(isPending);
  useEffect(() => {
    if (prevIsPendingRef.current && !isPending) {
      resetStreamingSections();
    }
    prevIsPendingRef.current = isPending;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending]);

  // SSE stream subscription for pending jotdocs
  const sseAbortControllerRef = useRef<AbortController | null>(null);
  const activeSseThreadRef = useRef<string | null>(null);

  // Subscribe to SSE stream when jotdoc is pending
  useEffect(() => {
    if (!isPending || !threadId || !jotdocId) {
      // Clean up any existing connection if not pending
      if (sseAbortControllerRef.current) {
        sseAbortControllerRef.current.abort();
        sseAbortControllerRef.current = null;
      }
      activeSseThreadRef.current = null;
      return;
    }

    if (activeSseThreadRef.current === threadId && sseAbortControllerRef.current) {
      // Already connected for this thread, skip re-subscribing
      return;
    }
    activeSseThreadRef.current = threadId;

    const API_BASE_URL = import.meta.env.VITE_API_URL as string;
    const streamUrl = `/v2/provider/note-processing-workflows/stream/${threadId}`;

    // Clean up any existing connection
    if (sseAbortControllerRef.current) {
      sseAbortControllerRef.current.abort();
    }

    // Create new abort controller
    sseAbortControllerRef.current = new AbortController();

    // Start SSE subscription
    (async () => {
      try {
        const token = await getAccessToken();
        if (!token) {
          console.error('NoteJotDocView: Failed to get access token for SSE stream');
          return;
        }

        await fetchEventSource(`${API_BASE_URL}${streamUrl}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: sseAbortControllerRef.current!.signal,

          onopen: async response => {
            if (!response.ok || response.status !== 200) {
              console.error('NoteJotDocView: SSE stream connection failed', {
                status: response.status,
                statusText: response.statusText,
              });
              throw new Error(`Failed to connect: ${response.status} ${response.statusText}`);
            }
          },

          onmessage: event => {
            if (!event.data) return;

            try {
              const data = JSON.parse(event.data) as {
                type?: string;
                status?: string;
                task_id?: string;
                delta?: string;
                namespace?: string;
                stepName?: string;
                [key: string]: unknown;
              };

              // Process TEXT_MESSAGE_CONTENT deltas for streaming sections
              if (
                data.type === 'TEXT_MESSAGE_CONTENT' &&
                data.delta &&
                data.namespace === 'document'
              ) {
                processStreamingDelta(data.delta);
              }

              // Handle TEXT_MESSAGE_END - mark streaming as complete
              if (data.type === 'TEXT_MESSAGE_END' && data.namespace === 'document') {
                markStreamingComplete();
              }

              // Check if the event indicates completion
              // The exact event structure may vary, but we're looking for status changes
              if (
                data.status === 'completed' ||
                data.type === 'WORKFLOW_COMPLETED' ||
                data.type === 'TASK_COMPLETED' ||
                data.type === 'RUN_FINISHED' ||
                data.type === 'completion'
              ) {
                // Mark streaming as complete
                markStreamingComplete();

                // Invalidate encounter cache to get updated outputs
                dispatch(
                  encountersApi.util.invalidateTags([{ type: 'Encounter', id: encounterId }]),
                );

                // Refetch encounter to get updated outputs
                void refetchEncounter();

                // Refetch jotdoc query
                dispatch(jotdocsApi.util.invalidateTags([{ type: 'JotDoc', id: jotdocId }]));
                void refetchJotdoc();

                // Clean up SSE connection
                if (sseAbortControllerRef.current) {
                  sseAbortControllerRef.current.abort();
                  sseAbortControllerRef.current = null;
                }
                activeSseThreadRef.current = null;
              }
            } catch (error) {
              // Failed to parse SSE event - ignore
            }
          },

          onerror: error => {
            console.error('NoteJotDocView: SSE stream error', error);
            // Don't throw - let fetchEventSource handle reconnection
          },
        });
      } catch (error) {
        console.error('NoteJotDocView: Failed to subscribe to SSE stream', error);
        // Clean up on error
        if (sseAbortControllerRef.current) {
          sseAbortControllerRef.current.abort();
          sseAbortControllerRef.current = null;
        }
      }
    })();

    // Cleanup on unmount or when dependencies change
    return () => {
      if (sseAbortControllerRef.current) {
        sseAbortControllerRef.current.abort();
        sseAbortControllerRef.current = null;
      }
      activeSseThreadRef.current = null;
    };
  }, [
    isPending,
    threadId,
    jotdocId,
    encounterId,
    getAccessToken,
    dispatch,
    refetchEncounter,
    refetchJotdoc,
    taskId,
    processStreamingDelta,
    markStreamingComplete,
  ]);

  // Poll for jotdoc and encounter while pending (every 10 seconds) to check for status changes
  // We need to poll both because NoteDocumentPicker reads pending status from encounter.outputs
  useEffect(() => {
    if (!isPending || !jotdocId) {
      return;
    }

    // Poll immediately
    void refetchJotdoc();
    if (encounterId) {
      void refetchEncounter();
    }

    // Set up polling interval (every 10 seconds)
    const intervalId = window.setInterval(() => {
      void refetchJotdoc();
      if (encounterId) {
        void refetchEncounter();
      }
    }, 10000);

    // Set timeout to stop polling after 3 minutes
    const timeoutId = window.setTimeout(() => {
      // Polling expired after 3 minutes
    }, 3 * 60 * 1000);

    // Cleanup: clear interval and timeout on unmount or when jotdoc is no longer pending
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [isPending, jotdocId, encounterId, refetchJotdoc, refetchEncounter]);

  // When jotdoc status changes from pending to completed, refetch encounter to update NoteDocumentPicker
  useEffect(() => {
    if (jotdocStatus === 'completed' && encounterId) {
      // Jotdoc just completed, refetch encounter to update outputs in NoteDocumentPicker
      void refetchEncounter();
    }
  }, [jotdocStatus, encounterId, refetchEncounter]);


  // Detect misspellings in jotdoc content
  const misspellingData = useMemo(() => {
    if (!jotdoc?.content || !troubleWords) {
      return { misspelledMatches: [], correctSpellingMatches: [], spellingMap: {} };
    }
    return detectMisspellingsInMarkdown(jotdoc.content, troubleWords);
  }, [jotdoc?.content, troubleWords]);

  // Create word click handler for opening find/replace modal with misspelling context
  const handleWordClick = useMemo(() => {
    if (!troubleWords || !jotdoc) return undefined;

    return createMisspellingClickHandler(
      troubleWords,
      jotdoc.content,
      jotdoc.jotdoc_id,
      openModal,
      MODAL_TYPES.JOTDOC_FIND_REPLACE,
    );
  }, [troubleWords, jotdoc, openModal]);

  // Handle document selection from sidebar
  const handleDocumentSelect = useCallback(
    (docType: string, docId: string, isJotdoc?: boolean) => {
      // Check if it's a jotdoc using the flag (more reliable than type name)
      if (isJotdoc && docId && docId !== jotdocId) {
        navigate(`/jotdoc/${docId}`, {
          state: { noteId, encounterId },
        });
      } else if (docType?.toLowerCase() === 'note' && docId) {
        // Navigate to the specific note using its encounter_document_id
        navigate(`/note/${docId}`, {
          state: { encounterId },
        });
      } else if (!isJotdoc && noteId) {
        // Fallback: navigate to current note if we have noteId
        navigate(`/note/${noteId}`, {
          state: { encounterId },
        });
      }
    },
    [noteId, encounterId, navigate, jotdocId],
  );

  // Handle copy sections (all or selected)
  const handleCopySections = useCallback(
    async (copyAll = false) => {
      if (!jotdoc?.content) {
        enqueueSnackbar('No content to copy', { variant: 'warning' });
        return;
      }

      const result = await copyJotDocSections({
        jotdocContent: jotdoc.content,
        jotdocTitle: jotdoc.title,
        selectedSectionIds: selectedSections,
        copyAll,
        blockNoteEditors: blockNoteEditorsRef.current,
        userPreferences,
        jotdocId: jotdoc.jotdoc_id,
        encounterId: encounterId,
        getDocVersion: () => '0', // Document version tracking can be added later if needed
      });

      if (result.success) {
        enqueueSnackbar(result.message, {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(result.message, {
          variant: result.message.includes('selected') ? 'warning' : 'error',
        });
      }
    },
    [jotdoc, selectedSections, userPreferences, encounterId, enqueueSnackbar],
  );

  // Handle find/replace - open modal with markdown content
  const handleFindReplace = useCallback(() => {
    if (!jotdoc?.content || !jotdocId) {
      enqueueSnackbar('No content available', { variant: 'warning' });
      return;
    }

    openModal(MODAL_TYPES.JOTDOC_FIND_REPLACE, {
      markdown: jotdoc.content,
      jotdocId,
    });
  }, [jotdoc, jotdocId, openModal, enqueueSnackbar]);

  // Watch for find/replace actions and apply them
  useEffect(() => {
    if (
      !findReplaceAction ||
      findReplaceAction.target !== 'jotdoc' ||
      findReplaceAction.jotdocId !== jotdocId ||
      !jotdoc?.content
    ) {
      return;
    }

    const applyFindReplace = async () => {
      try {
        // Apply find/replace to the markdown
        const result = findReplaceInMarkdown(
          jotdoc.content,
          findReplaceAction.find,
          findReplaceAction.replace,
          findReplaceAction.caseSensitive,
        );

        // Only save if content actually changed
        if (result.updatedMarkdown !== jotdoc.content) {
          // Save the updated markdown to the backend
          // RTK Query optimistic update will update the cache, which will trigger re-render
          await saveMarkdown({
            jotdocId: jotdocId!,
            updatedMarkdown: result.updatedMarkdown,
            onSuccess: () => {
              enqueueSnackbar('Find/replace completed successfully', { variant: 'success' });
            },
            onError: error => {
              console.error('Failed to save find/replace changes:', error);
              enqueueSnackbar('Failed to save changes', { variant: 'error' });
            },
          });
        }

        // Clear the find/replace action to prevent re-running
        dispatch(clearFindReplace());
      } catch (error) {
        console.error('Error applying find/replace:', error);
        enqueueSnackbar('Failed to apply find/replace changes', { variant: 'error' });
        dispatch(clearFindReplace());
      }
    };

    applyFindReplace();
  }, [findReplaceAction, jotdoc, jotdocId, dispatch, saveMarkdown, enqueueSnackbar]);

  // Handle open copy/paste settings
  const handleOpenCopyPasteSettings = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      openModal(MODAL_TYPES.COPY_PASTE_PREFERENCES, {});
    },
    [openModal],
  );

  // Handle navigate to note
  const handleViewNote = useCallback(() => {
    if (!noteId) {
      enqueueSnackbar('Note not available', { variant: 'warning' });
      return;
    }
    navigate(`/note/${noteId}`, {
      state: { encounterId },
    });
  }, [noteId, encounterId, navigate, enqueueSnackbar]);

  // Parse sections from jotdoc content for table of contents
  // Use streaming sections if available and pending, otherwise use normal sections
  const normalSections = useJotdocSections(jotdoc?.content);
  const hasStreamingSections = isPending && streamingSections.length > 0;
  const showInitialDocumentSpinner = isPending && streamingSections.length === 0;
  const streamingIndicatorVisible = isPending && isStreamingSections && hasStreamingSections;

  const streamingJotdoc = useMemo(() => {
    if (!hasStreamingSections) {
      return null;
    }

    const noteStartIndex = streamingAccumulatedContent.indexOf('<note>');
    const noteEndIndex = streamingAccumulatedContent.indexOf('</note>');
    const noteContent =
      noteStartIndex !== -1 && noteEndIndex !== -1
        ? streamingAccumulatedContent.substring(noteStartIndex + '<note>'.length, noteEndIndex)
        : streamingAccumulatedContent;

    return {
      jotdoc_id: jotdocId || '',
      title: jotdocResponse?.jotdoc?.title || 'Generating note...',
      content: noteContent,
      created_at: jotdocResponse?.jotdoc?.created_at || '',
    };
  }, [
    hasStreamingSections,
    streamingAccumulatedContent,
    jotdocId,
    jotdocResponse?.jotdoc?.title,
    jotdocResponse?.jotdoc?.created_at,
  ]);

  const sections = hasStreamingSections ? streamingSections : normalSections.sections;
  const hasDocumentLayout = !jotdocLoading && !jotdocIsError && jotdoc !== null;

  // UILayout owns document scrolling; attach after the loading/error view exits.
  useEffect(() => {
    if (!hasDocumentLayout) return;
    const scrollContainer = document.getElementById('main-content');
    if (!scrollContainer) return;

    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 800); // Wait 800ms after scroll stops before showing indicators (accounts for smooth scroll animations)
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [hasDocumentLayout]);

  // Calculate gutter left position based on sections container position
  useEffect(() => {
    const updateGutterPosition = () => {
      if (sectionsContainerRef.current) {
        // Find the first section element to get its position
        const firstSection = sectionsContainerRef.current.querySelector(
          '[id^="section-"]',
        ) as HTMLElement;
        if (firstSection) {
          const sectionRect = firstSection.getBoundingClientRect();
          // Gutter is positioned absolutely at left: 0 within section
          // Sections have pl: 8 (64px padding-left), so gutter is at section's left edge
          const newLeft = sectionRect.left;
          // Only update if position actually changed to prevent unnecessary re-renders
          setGutterLeft(prev => (prev !== newLeft ? newLeft : prev));
        } else {
          // Fallback: use container position + section padding (64px = pl: 8)
          const rect = sectionsContainerRef.current.getBoundingClientRect();
          const newLeft = rect.left + 64;
          // Only update if position actually changed to prevent unnecessary re-renders
          setGutterLeft(prev => (prev !== newLeft ? newLeft : prev));
        }
      }
    };

    // Initial calculation
    updateGutterPosition();

    // Use requestAnimationFrame to ensure DOM is ready
    const rafId = requestAnimationFrame(updateGutterPosition);

    // Throttle scroll/resize events to prevent excessive updates
    let ticking = false;
    const throttledUpdate = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateGutterPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('resize', throttledUpdate);
    window.addEventListener('scroll', throttledUpdate, true);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', throttledUpdate);
      window.removeEventListener('scroll', throttledUpdate, true);
    };
  }, [jotdoc]);

  // Handle section click - scroll to section
  const handleSectionClick = useCallback((sectionId: string) => {
    const sectionElement = document.getElementById(`section-${sectionId}`);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth', inline: 'start' });
    }
  }, []);

  // Handle sidebar icon click - toggle section selection
  const handleSidebarIconClick = useCallback((event: React.MouseEvent, sectionId: string) => {
    event.stopPropagation();
    setSelectedSections(prevSelectedSections => {
      if (prevSelectedSections.includes(sectionId)) {
        return prevSelectedSections.filter(id => id !== sectionId);
      } else {
        return [...prevSelectedSections, sectionId];
      }
    });
  }, []);

  // Loading state
  if (jotdocLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 0 }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (jotdocIsError || (!jotdocLoading && !jotdoc)) {
    const errorMessage =
      jotdocQueryError && 'data' in jotdocQueryError
        ? (jotdocQueryError.data as any)?.message || 'Failed to load document'
        : 'Document not found';
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: 0,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h6" color="error">
          {errorMessage}
        </Typography>
      </Box>
    );
  }

  // Determine if we should show sidebar (only if we have encounter context)
  // Show sidebar if we have encounter data with outputs, or if we're still loading (to show loading state)
  const showSidebar = Boolean(encounterData?.outputs && encounterData.outputs.length > 0);

  return (
    <LayoutWrapper>
      <LayoutWrapper.TopBar>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            minWidth: 'max-content',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ml: { xs: 0, md: 4 },
              order: { xs: 2, md: 1 },
              pr: { xs: 3, sm: 4 },
            }}
          >
            <RoundedButton
              variant="outlined"
              sx={{
                fontSize: '0.85rem',
                fontWeight: '700',
                whiteSpace: 'nowrap',
                bgcolor: 'backgroundColors.secondary',
                borderColor: 'borderColors.lightprimary',
                border: '0.1px solid',
                px: 2,
              }}
              onClick={handleFindReplace}
            >
              <SpellcheckIcon
                sx={{
                  color: theme.palette.primary.light,
                  fontSize: '1rem',
                  paddingRight: 0.25,
                }}
              />
              Find/replace
            </RoundedButton>

            {selectedSections.length > 0 && (
              <RoundedButton
                variant="outlined"
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  whiteSpace: 'nowrap',
                  px: 2,
                  bgcolor: 'backgroundColors.secondary',
                  borderColor: 'borderColors.lightprimary',
                  border: '0.1px solid',
                  ml: 1,
                }}
                onClick={() => handleCopySections(false)}
              >
                <ContentCopyIcon
                  sx={{
                    color: theme.palette.primary.light,
                    fontSize: '1rem',
                    paddingRight: 0.25,
                  }}
                />
                Copy Selected Sections ({selectedSections.length})
              </RoundedButton>
            )}

            <RoundedButton
              variant="outlined"
              sx={{
                fontSize: '0.85rem',
                fontWeight: '700',
                whiteSpace: 'nowrap',
                px: 2,
                bgcolor: 'backgroundColors.secondary',
                borderColor: 'borderColors.lightprimary',
                border: '0.1px solid',
                ml: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
              onClick={() => handleCopySections(true)}
            >
              <ContentCopyIcon
                sx={{
                  color: theme.palette.primary.light,
                  fontSize: '1rem',
                }}
              />
              Copy All Sections
              <Box
                component="span"
                onClick={handleOpenCopyPasteSettings}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  ml: 'auto',
                  pl: 1,
                  borderLeft: '1px solid',
                  borderLeftColor: 'divider',
                  '&:hover': {
                    opacity: 0.7,
                  },
                }}
              >
                <SettingsIcon
                  sx={{
                    color: theme.palette.primary.light,
                    fontSize: '0.9rem',
                  }}
                />
              </Box>
            </RoundedButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              mr: { xs: 0, md: 4 },
              order: { xs: 1, md: 2 },
            }}
          >
            {noteId && (
              <RoundedButton
                variant="outlined"
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  whiteSpace: 'nowrap',
                  px: 2,
                  bgcolor: 'backgroundColors.secondary',
                  borderColor: 'borderColors.lightprimary',
                  border: '0.1px solid',
                }}
                onClick={handleViewNote}
              >
                <DescriptionIcon
                  sx={{
                    color: theme.palette.primary.light,
                    fontSize: '1rem',
                    paddingRight: 0.25,
                  }}
                />
                View Note
              </RoundedButton>
            )}
          </Box>
        </Box>
      </LayoutWrapper.TopBar>
      <LayoutWrapper.LeftSidebar>
        <Box className="flex-1" sx={{ ...hiddenScrollbarStyles, height: '100%', overflow: 'auto' }}>
          {/* Show sidebar if we have encounter data or are loading it */}
          {(showSidebar || encounterLoading) && (
            <Box>
              <NoteDocumentPicker
                selectedDocId={jotdocId}
                onSelectDocument={handleDocumentSelect}
                encounterData={(encounterData as any) || null}
                parentNoteId={noteId}
                extras={noteData?.content?.extras}
              />
            </Box>
          )}
          <Typography
            align="center"
            sx={{
              fontWeight: 'bold',
              mb: 2,
              color: 'textColors.primaryText',
              mt: showSidebar ? 2 : 0,
            }}
          >
            Document Sections
          </Typography>
          <ul>
            {sections.map(section => {
              const isSelected = selectedSections.includes(section.id);
              return (
                <ListItem key={section.id} sx={{ width: '100%' }}>
                  <Box
                    onClick={() => handleSectionClick(section.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      px: 1,
                      fontSize: 'text-base',
                      color: 'textColors.darkHeader',
                      borderRadius: 'lg',
                      '&:hover': {
                        cursor: 'pointer',
                      },
                    }}
                  >
                    <Box onClick={event => handleSidebarIconClick(event, section.id)}>
                      <SVGIcon name={isSelected ? 'check-filled' : 'check-unfilled'} />
                    </Box>
                    <Typography
                      variant="body1"
                      component="span"
                      color="backgroundColors.brand"
                      sx={{
                        ml: 3,
                        textTransform: 'capitalize',
                        fontStyle: 'normal',
                        fontWeight: isSelected ? 'bold' : 'normal',
                      }}
                    >
                      {/* Remove any text between parentheses, e.g. (Customized) */}
                      {section.title?.replace(/\s*\([^)]*\)/g, '')}
                    </Typography>
                  </Box>
                </ListItem>
              );
            })}
          </ul>
        </Box>
      </LayoutWrapper.LeftSidebar>
      <LayoutWrapper.MainContent>
        <Box data-testid="jotdoc-content" sx={{ minWidth: 0, pb: 2 }}>
            <Box ref={sectionsContainerRef} sx={{ my: 2, mx: { xs: 1, sm: 2 } }}>
              {showInitialDocumentSpinner ? (
              <Box
                sx={{
                  minHeight: 360,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  borderRadius: 2,
                  border: theme => `1px dashed ${theme.palette.divider}`,
                  backgroundColor: theme => theme.palette.background.paper,
                }}
              >
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary">
                  Document loading...
                </Typography>
              </Box>
            ) : (
              <JotDocNoteSectionView
                jotdoc={hasStreamingSections && streamingJotdoc ? streamingJotdoc : jotdoc}
                loading={jotdocLoading && !isStreamingSections}
                encounterId={encounterId}
                selectedSectionIds={selectedSections}
                isScrolling={isScrolling}
                troubleWords={troubleWords}
                misspelledWords={misspellingData.misspelledMatches}
                onWordClick={handleWordClick}
                onToggleSectionSelection={(sectionId: string) => {
                  // Wrapper to match handleSidebarIconClick signature
                  const syntheticEvent = {
                    stopPropagation: () => {},
                  } as React.MouseEvent;
                  handleSidebarIconClick(syntheticEvent, sectionId);
                }}
                onEditorsReady={editorsMap => {
                  // Update our ref with the latest editors map
                  blockNoteEditorsRef.current.clear();
                  editorsMap.forEach((editor, sectionId) => {
                    blockNoteEditorsRef.current.set(sectionId, editor);
                  });
                }}
                streamingIndicator={
                  streamingIndicatorVisible ? (
                    <Box
                      sx={{
                        mt: 2,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Generating note...
                      </Typography>
                    </Box>
                  ) : undefined
                }
              />
            )}
          </Box>
          {/* Next indicator button - fixed position in left gutter */}
          {jotdocId && gutterLeft > 0 && (
            <Box
              sx={{
                position: 'fixed',
                bottom: 16,
                left: `${gutterLeft}px`,
                width: '64px', // Match gutter width
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                pointerEvents: 'auto',
              }}
            >
              <NextIndicatorButton jotdocId={jotdocId} />
            </Box>
          )}
        </Box>
      </LayoutWrapper.MainContent>
    </LayoutWrapper>
  );
};

export default NoteJotDocView;
